import { useEffect } from 'react';

/**
 * Custom hook pentru actualizarea dinamică a meta tags-urilor SEO
 * @param {Object} options - Opțiuni SEO
 * @param {string} options.title - Titlul paginii
 * @param {string} options.description - Descrierea paginii
 * @param {string} options.keywords - Cuvinte cheie (separate prin virgulă)
 * @param {string} options.image - URL imagine pentru Open Graph
 * @param {string} options.url - URL canonic
 * @param {string} options.type - Tip pagină (website, product, article)
 */
export const useSEO = ({
    title,
    ogTitle,
    description,
    keywords,
    image,
    url,
    type = 'website'
}) => {
    useEffect(() => {
        // Base title
        const baseTitle = 'Colectarium';
        const fullTitle = title ? `${title} | ${baseTitle}` : baseTitle;
        const shareTitle = ogTitle || fullTitle;
        
        // Update document title
        document.title = fullTitle;
        
        // Helper to update or create meta tag
        const updateMeta = (property, content, isProperty = false) => {
            if (!content) return;
            
            const attr = isProperty ? 'property' : 'name';
            let meta = document.querySelector(`meta[${attr}="${property}"]`);
            
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute(attr, property);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', content);
        };
        
        // Update meta tags
        if (description) {
            updateMeta('description', description);
            updateMeta('og:description', description, true);
            updateMeta('twitter:description', description);
        }
        
        if (keywords) {
            updateMeta('keywords', keywords);
        }
        
        // Open Graph - use shareTitle for social media
        updateMeta('og:title', shareTitle, true);
        updateMeta('og:type', type, true);
        
        if (url) {
            updateMeta('og:url', url, true);
            // Update canonical link
            let canonical = document.querySelector('link[rel="canonical"]');
            if (!canonical) {
                canonical = document.createElement('link');
                canonical.setAttribute('rel', 'canonical');
                document.head.appendChild(canonical);
            }
            canonical.setAttribute('href', url);
        }
        
        if (image) {
            updateMeta('og:image', image, true);
            updateMeta('twitter:image', image);
        }
        
        // Twitter - use shareTitle for social media
        updateMeta('twitter:title', shareTitle);
        updateMeta('twitter:card', 'summary_large_image');
        
    }, [title, ogTitle, description, keywords, image, url, type]);
};

/**
 * Generează meta description optimizat pentru SEO
 * @param {string} text - Text de bază
 * @param {number} maxLength - Lungime maximă (default 160)
 * @returns {string} - Descriere optimizată
 */
export const generateMetaDescription = (text, maxLength = 160) => {
    if (!text) return '';
    
    // Remove HTML tags
    const cleanText = text.replace(/<[^>]*>/g, '');
    
    // Trim and limit length
    if (cleanText.length <= maxLength) return cleanText;
    
    // Cut at last space before maxLength
    const trimmed = cleanText.substring(0, maxLength);
    const lastSpace = trimmed.lastIndexOf(' ');
    
    return lastSpace > 0 ? trimmed.substring(0, lastSpace) + '...' : trimmed + '...';
};

/**
 * Keywords predefinite pentru diferite categorii
 */
export const SEO_KEYWORDS = {
    base: 'colectarium, numismatică, faleristică, efecte militare, medalii, ordine, monede, bancnote, insigne, vânzare, cumpăr, vând, licitații, licitație, piese de colecție, colecționari, România',
    numismatica: 'numismatică, monede, bancnote, numismatic, monede vechi, monede românești, monede de colecție, medalii comemorative, cumpăr monede, vând monede, monede antice, monede rare, bancnote vechi, bancnote românești',
    faleristica: 'faleristică, medalii, decorații, ordine, insigne, medalii militare, decorații România, ordine militare, cumpăr medalii, vând decorații, ordine și medalii, insigne militare, insigne de colecție, ordine românești',
    militare: 'efecte militare, uniforme militare, echipament militar, militaria, uniforme vechi, cumpăr uniforme, vând militare, armament de colecție, insigne militare, grade militare, căști militare',
    diverse: 'antichități, piese de colecție, vintage, retro, obiecte vechi, colecționari',
    licitatii: 'licitații online, licitație, auction, licitații numismatică, licitații medalii, licitații militare, bid, ofertă, licitații monede, licitații ordine',
    vanzari: 'vânzare, vânzări, preț fix, cumpără acum, piese de vânzare, vând piese colecție, cumpăr monede, vând medalii, cumpăr insigne'
};

/**
 * Generează JSON-LD pentru un produs (listing)
 */
export const generateProductSchema = (listing) => {
    if (!listing) return null;
    
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": listing.title,
        "description": listing.description,
        "image": listing.images?.[0] || '',
        "sku": listing.listing_id,
        "brand": {
            "@type": "Brand",
            "name": "Colectarium"
        },
        "offers": {
            "@type": listing.listing_type === 'auction' ? "AggregateOffer" : "Offer",
            "url": `https://colectarium.ro/listing/${listing.listing_id}`,
            "priceCurrency": "RON",
            "price": listing.current_price || listing.price || listing.starting_price,
            "availability": listing.status === 'active' 
                ? "https://schema.org/InStock" 
                : "https://schema.org/SoldOut",
            "seller": {
                "@type": "Person",
                "name": listing.seller_username || listing.seller_name
            }
        },
        "category": listing.category
    };
};

/**
 * Generează JSON-LD pentru un breadcrumb
 */
export const generateBreadcrumbSchema = (items) => {
    if (!items || items.length === 0) return null;
    
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
        }))
    };
};

export default useSEO;
